import BaseAPI from './base';
import { samplePosts, sampleCategories } from '../data/blogPosts';

// Use local blog data (set to false to use external API)
const USE_LOCAL_DATA = process.env.REACT_APP_USE_SAMPLE_BLOG_DATA !== 'false';

class BlogAPI extends BaseAPI {
    constructor() {
        super({
            baseUrl: process.env.REACT_APP_API_BASE_URL || 'https://api.example.com',
            cacheExpiration: 10 * 60 * 1000, // 10 minutes for blog content
            debug: process.env.NODE_ENV === 'development'
        });
    }

    /**
     * Get all blog posts with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Blog posts data
     */
    async getPosts(params = {}) {
        if (USE_LOCAL_DATA) {
            const { page = 1, limit = 12, category = '', featured = false } = params;
            let filteredPosts = [...samplePosts];

            if (category) {
                filteredPosts = filteredPosts.filter(post => post.category.slug === category);
            }

            if (featured) {
                filteredPosts = filteredPosts.filter(post => post.featured);
            }

            const startIndex = (page - 1) * limit;
            const endIndex = startIndex + limit;
            const paginatedPosts = filteredPosts.slice(startIndex, endIndex);

            return Promise.resolve({
                results: paginatedPosts,
                totalPages: Math.ceil(filteredPosts.length / limit),
                currentPage: page,
                total: filteredPosts.length
            });
        }

        const { page = 1, limit = 12, category = '', featured = false } = params;
        const queryParams = new URLSearchParams({
            page,
            limit,
            ...(category && { category }),
            ...(featured && { featured: 'true' })
        });

        return this.request(`/api/blog/posts?${queryParams}`);
    }

    /**
     * Get a single blog post by slug
     * @param {string} slug - Post slug
     * @returns {Promise<Object>} Blog post data
     */
    async getPostBySlug(slug) {
        if (USE_LOCAL_DATA) {
            const post = samplePosts.find(p => p.slug === slug);
            return Promise.resolve(post || null);
        }
        return this.request(`/api/blog/posts/${slug}`);
    }

    /**
     * Get blog categories
     * @returns {Promise<Array>} Categories list
     */
    async getCategories() {
        if (USE_LOCAL_DATA) {
            return Promise.resolve(sampleCategories);
        }
        return this.request('/api/blog/categories');
    }

    /**
     * Get posts by category
     * @param {string} categorySlug - Category slug
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Blog posts data
     */
    async getPostsByCategory(categorySlug, params = {}) {
        if (USE_LOCAL_DATA) {
            const { page = 1, limit = 12 } = params;
            const category = sampleCategories.find(c => c.slug === categorySlug);
            const filteredPosts = samplePosts.filter(post => post.category.slug === categorySlug);
            
            const startIndex = (page - 1) * limit;
            const endIndex = startIndex + limit;
            const paginatedPosts = filteredPosts.slice(startIndex, endIndex);

            return Promise.resolve({
                category,
                results: paginatedPosts,
                totalPages: Math.ceil(filteredPosts.length / limit),
                currentPage: page,
                total: filteredPosts.length
            });
        }

        const { page = 1, limit = 12 } = params;
        const queryParams = new URLSearchParams({ page, limit });
        return this.request(`/api/blog/categories/${categorySlug}/posts?${queryParams}`);
    }

    /**
     * Get related posts
     * @param {string} postId - Post ID
     * @param {number} limit - Number of related posts
     * @returns {Promise<Array>} Related posts
     */
    async getRelatedPosts(postId, limit = 3) {
        if (USE_LOCAL_DATA) {
            const currentPost = samplePosts.find(p => p._id === postId);
            if (!currentPost) return Promise.resolve([]);

            const relatedPosts = samplePosts
                .filter(p => p._id !== postId && p.category.slug === currentPost.category.slug)
                .slice(0, limit);

            return Promise.resolve(relatedPosts);
        }
        return this.request(`/api/blog/posts/${postId}/related?limit=${limit}`);
    }

    /**
     * Search blog posts
     * @param {string} query - Search query
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Search results
     */
    async searchPosts(query, params = {}) {
        if (USE_LOCAL_DATA) {
            const { page = 1, limit = 12 } = params;
            const lowerQuery = query.toLowerCase();
            const filteredPosts = samplePosts.filter(post => 
                post.title.toLowerCase().includes(lowerQuery) ||
                post.excerpt.toLowerCase().includes(lowerQuery) ||
                post.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
            );

            const startIndex = (page - 1) * limit;
            const endIndex = startIndex + limit;
            const paginatedPosts = filteredPosts.slice(startIndex, endIndex);

            return Promise.resolve({
                results: paginatedPosts,
                totalPages: Math.ceil(filteredPosts.length / limit),
                currentPage: page,
                total: filteredPosts.length
            });
        }

        const { page = 1, limit = 12 } = params;
        const queryParams = new URLSearchParams({ q: query, page, limit });
        return this.request(`/api/blog/search?${queryParams}`);
    }

    /**
     * Get featured posts
     * @param {number} limit - Number of featured posts
     * @returns {Promise<Array>} Featured posts
     */
    async getFeaturedPosts(limit = 3) {
        if (USE_LOCAL_DATA) {
            const featuredPosts = samplePosts.filter(p => p.featured).slice(0, limit);
            return Promise.resolve(featuredPosts);
        }
        return this.request(`/api/blog/featured?limit=${limit}`);
    }
}

export default new BlogAPI();
